import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, ShieldCheck, ShieldAlert, FileDown, RefreshCw, FileText, Info, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatBytes = (n) => {
  if (!n || typeof n !== 'number') return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatRelative = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
};

export default function SOWHealthTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/sow/template-health`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Health check failed');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { run(); }, [run]);

  const download = (path, filename) => {
    const url = `${API_URL}${path}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    a.click();
  };

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Running health check…
      </div>
    );
  }
  if (!data) return null;

  const renderChecks = (name, checks, kind) => {
    const entries = Object.entries(checks || {});
    const passedN = entries.filter(([, v]) => v.passed).length;
    const total = entries.length;
    return (
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-[#0F2040] text-sm">
            {kind === 'rendered'
              ? <ShieldCheck className="w-4 h-4 text-emerald-600" />
              : <FileText className="w-4 h-4 text-slate-500" />}
            {name}
            <span className="ml-auto text-[11px] text-slate-500 font-normal">
              {passedN}/{total} checks pass
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {entries.map(([id, v]) => (
            <div key={id}
              className={`flex items-start gap-2 text-[11px] px-2 py-1.5 rounded ${v.passed ? 'bg-emerald-50/60' : 'bg-rose-50/60'}`}
              data-testid={`health-check-${id}`}>
              {v.passed
                ? <ShieldCheck className="w-3 h-3 text-emerald-600 mt-0.5 shrink-0" />
                : <ShieldAlert className="w-3 h-3 text-rose-600 mt-0.5 shrink-0" />}
              <div className="flex-1">
                <div className={v.passed ? 'text-emerald-800' : 'text-rose-800 font-medium'}>
                  {v.label}
                </div>
                {v.count > 0 && (
                  <div className="text-[10px] text-slate-500">
                    {v.count} occurrence{v.count === 1 ? '' : 's'} found
                  </div>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4" data-testid="sow-health-tab">
      {data.template_exists === false && (
        <Card className="border border-rose-300 bg-rose-50/60" data-testid="template-missing-alert">
          <CardContent className="py-3 px-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm">
              <div className="font-semibold text-rose-800">SOW Master Template MISSING from this deployment</div>
              <div className="text-[11px] text-rose-700 mt-1">
                {data.error || `Expected at ${data.template_path}. SOW exports are silently rendering with the legacy builder, which produces the wrong client-facing format. Re-deploy with backend/assets/Seertech_SOW_Template.docx bundled.`}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className={`border ${data.overall_passed ? 'border-emerald-300 bg-emerald-50/40' : 'border-amber-300 bg-amber-50/40'}`}>
        <CardContent className="py-3 px-4 flex items-center gap-3">
          {data.overall_passed
            ? <ShieldCheck className="w-5 h-5 text-emerald-600" />
            : <ShieldAlert className="w-5 h-5 text-amber-600" />}
          <div className="flex-1">
            <div className={`text-sm font-semibold ${data.overall_passed ? 'text-emerald-800' : 'text-amber-800'}`}
                 data-testid="health-overall">
              {data.overall_passed
                ? 'Rendered SOW passes all template drift checks'
                : 'Rendered SOW has guardrail failures — review below'}
            </div>
            <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-0.5 flex-wrap">
              <Info className="w-3 h-3" />
              Master template path: <span className="font-mono">{data.template_path?.split('/').pop()}</span>
              {data.template_exists !== false && data.template_size_bytes ? (
                <span
                  className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200"
                  data-testid="template-size-badge"
                >
                  <ShieldCheck className="w-2.5 h-2.5" />
                  Template OK · {formatBytes(data.template_size_bytes)}
                  {formatRelative(data.template_mtime) ? ` · updated ${formatRelative(data.template_mtime)}` : ''}
                </span>
              ) : null}
            </div>
          </div>
          <Button type="button" size="sm" variant="outline" onClick={run}
            disabled={loading}
            className="h-7 text-[11px]"
            data-testid="health-refresh-btn">
            {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Re-run
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {renderChecks('Master template (source)', data.checks?.master_template, 'master')}
        {renderChecks('Rendered sample (client-facing)', data.checks?.rendered_sample, 'rendered')}
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-[#0F2040]">Download for side-by-side review</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline"
            onClick={() => download('/api/sow/template-health/master', 'SOW_Master_Template.docx')}
            className="h-7 text-[11px]" data-testid="health-dl-master">
            <FileDown className="w-3 h-3 mr-1" />
            Master template DOCX
          </Button>
          <Button type="button" size="sm" variant="outline"
            onClick={() => download('/api/sow/template-health/sample', 'SOW_Health_Sample.docx')}
            className="h-7 text-[11px]" data-testid="health-dl-sample">
            <FileDown className="w-3 h-3 mr-1" />
            Rendered sample DOCX
          </Button>
          <div className="text-[10px] text-slate-500 ml-auto self-center">
            Open both in Word with track-changes to spot any drift.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
